import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Paper,
  Tooltip,
  Link
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { es, ca } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

const MessageItem = ({ 
  message, 
  currentUser, 
  onDelete, 
  onEdit, 
  showSender = true,
  isFollowUp = false
}) => {
  const { t, i18n } = useTranslation(['common']);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  
  const isOwnMessage = message.sender._id === currentUser._id;
  const menuOpen = Boolean(anchorEl);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'es':
        return es;
      case 'ca':
        return ca;
      default:
        return undefined;
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedContent(message.content);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    onDelete(message._id);
    handleMenuClose();
  };

  const handleSaveEdit = () => {
    if (editedContent.trim() !== message.content) {
      onEdit(message._id, editedContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent(message.content);
  };

  // URL regex pattern to match URLs starting with http://, https://, or www.
  const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;

  // Function to parse text and convert URLs to clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return '';

    const parts = text.split(urlPattern);
    return parts.map((part, index) => {
      if (!part) return null;
      
      // Check if this part matches a URL pattern
      if (part.match(urlPattern)) {
        // Add https:// if the URL starts with www.
        const href = part.startsWith('www.') ? `https://${part}` : part;
        return (
          <Link
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: isOwnMessage ? 'inherit' : 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  return (
    <Box 
      sx={{ 
        display: 'flex',
        mb: isFollowUp ? 0.5 : 1.5,
        mt: isFollowUp ? 0 : 1,
        position: 'relative',
        pl: isFollowUp ? 7 : 0,
        '&:hover .message-options': {
          opacity: 1
        }
      }}
    >
      {showSender && (
        <Avatar
          src={message.sender.imageUrl}
          alt={message.sender.name || message.sender.username}
          sx={{ 
            mr: 1.5, 
            width: 40, 
            height: 40,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}
        >
          {(message.sender.name || message.sender.username)?.[0]}
        </Avatar>
      )}
      
      <Box sx={{ flex: 1 }}>
        {showSender && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
            <Typography 
              variant="subtitle2" 
              fontWeight="bold" 
              sx={{ mr: 1 }}
            >
              {message.sender.name || message.sender.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(message.createdAt), { 
                addSuffix: true,
                locale: getDateLocale()
              })}
            </Typography>
            {message.isEdited && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({t('comms.edited')})
              </Typography>
            )}
          </Box>
        )}
        
        {isEditing ? (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              size="small"
              autoFocus
              variant="outlined"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <IconButton size="small" onClick={handleSaveEdit} color="primary">
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" component="div" sx={{ 
            whiteSpace: 'pre-wrap',
            py: 1,
            px: 1.5,
            borderRadius: isOwnMessage ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
            backgroundColor: isOwnMessage ? 'primary.light' : 'background.paper',
            color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            maxWidth: '80%',
            wordBreak: 'break-word',
            display: 'inline-block'
          }}>
            {renderTextWithLinks(message.content)}
          </Typography>
        )}
      </Box>
      
      {isOwnMessage && !isEditing && (
        <Box 
          className="message-options"
          sx={{ 
            position: 'absolute',
            right: 0,
            top: 0,
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out'
          }}
        >
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleEditClick} dense>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              {t('comms.edit')}
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} dense>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              {t('comms.delete')}
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

export default MessageItem; 